// Validate correspondence between two equally sampled, consistently oriented rails.
// Containment alone cannot detect folded or criss-crossed satin columns.
export function satinRailQuality(left, right) {
  if (left.length !== right.length || left.length < 5) return null;
  const n = left.length;
  const cross = (a, b) => a.x * b.y - a.y * b.x;
  const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
  const middle = left.map((a, i) => ({
    x: (a.x + right[i].x) / 2,
    y: (a.y + right[i].y) / 2,
  }));
  const widths = left.map((a, i) =>
    Math.hypot(a.x - right[i].x, a.y - right[i].y)
  );
  const max = Math.max(...widths),
    epsilon = Math.max(max * 1e-6, 1e-8);
  if (max < 1e-8) return null;
  let sign = 0,
    skew = 0,
    turn = 0,
    count = 0;
  const rungs = [];
  for (let i = 1; i < n - 1; i++) {
    if (widths[i] < epsilon) return null; // Interior pinch / overlapping rails.
    const chord = sub(right[i], left[i]);
    const tangent = sub(middle[i + 1], middle[i - 1]);
    const len = Math.hypot(tangent.x, tangent.y);
    if (len < epsilon) continue;
    const side = cross(tangent, chord) / (len * widths[i]);
    if (Math.abs(side) < 0.02) return null; // Correspondence runs along, not across, the column.
    if (sign && Math.sign(side) !== sign) return null;
    sign = Math.sign(side);
    skew +=
      Math.abs(tangent.x * chord.x + tangent.y * chord.y) / (len * widths[i]);
    if (rungs.length) {
      const previous = rungs[rungs.length - 1];
      turn +=
        Math.abs(cross(previous, chord)) /
        (Math.hypot(previous.x, previous.y) * widths[i]);
    }
    rungs.push(chord);
    count++;
  }
  const intersects = (a, b, c, d) => {
    const ab = sub(b, a),
      cd = sub(d, c),
      den = cross(ab, cd);
    if (Math.abs(den) < 1e-12) return false;
    const t = cross(sub(c, a), cd) / den,
      u = cross(sub(c, a), ab) / den;
    return t > 1e-6 && t < 1 - 1e-6 && u > 1e-6 && u < 1 - 1e-6;
  };
  for (let i = 1; i < n - 1; i++)
    for (let j = i + 1; j < n - 1; j++)
      if (intersects(left[i], right[i], left[j], right[j])) return null;
  // Reject rails crossing each other away from shared tip endpoints.
  for (let i = 0; i < n - 1; i++)
    for (let j = 0; j < n - 1; j++)
      if (intersects(left[i], left[i + 1], right[j], right[j + 1])) return null;
  return count ? skew / count + (0.25 * turn) / count : null;
}
